"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, PhoneCall, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Cta() {
  const shouldReduceMotion = useReducedMotion();
  const containerMotion = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="cta" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-t border-border">
      <div className="container mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerMotion} className="relative rounded-2xl bg-muted/60 border border-border p-8 sm:p-12 lg:p-16">
          {/* Subtle Decorative Pristine Datum Bar */}
          <div className="w-9 h-0.5 bg-primary mb-6" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-secondary border border-border px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground mb-4">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>Auckland Property Protection</span>
              </div>

              <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground text-balance">
                Protect your home from unwanted pests
              </h2>

              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                Book a prompt, targeted treatment for your property or pair it
                with an exterior surface wash for clean walls and a barrier that
                lasts.
              </p>

              <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-2xl">
                Eco Home Services uses safe, targeted treatments across Auckland
                for rodents, ants, cockroaches, and wasps with straightforward
                pricing and clear communication.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Button type="button" size="lg" onClick={() => {
                if (typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("eco-open-quote-modal"));
                }
              }} className="inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground font-semibold px-6 py-3 shadow-xs hover:bg-primary/90 transition-[background-color,box-shadow]">
                  Get a free quote
                  <ArrowRight className="size-4" />
                </Button>

                <Button asChild variant="secondary" size="lg" className="rounded-md border border-border bg-secondary text-secondary-foreground font-medium px-6 py-3 hover:bg-accent hover:text-accent-foreground transition-colors">
                  <Link to="/ExteriorSurfaceCleaningPage" className="inline-flex items-center justify-center gap-2">
                    <Sparkles className="size-4 text-primary" />
                    Add exterior surface cleaning
                  </Link>
                </Button>
              </div>

              <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
                <span>Prefer to speak with our local team?</span>
                <a href="tel:0221002103" className="inline-flex items-center gap-1.5 font-semibold text-foreground hover:text-primary transition-colors underline underline-offset-4">
                  <PhoneCall className="size-3.5 text-primary" />
                  022 100 2103
                </a>
              </div>
            </div>

            {/* Practical Service Highlights Box */}
            <div className="lg:col-span-5">
              <div className="rounded-xl bg-card border border-border p-6 shadow-xs">
                <h3 className="font-default text-lg font-semibold text-card-foreground">
                  The dual protection advantage
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Removing dirt, mold, and moss from walls and pavers before
                  applying perimeter barrier treatments creates a cleaner surface
                  for longer-lasting pest defense.
                </p>

                <div className="mt-6 space-y-3">
                  <div data-index={0} className="flex items-start gap-3 text-sm text-card-foreground">
                    <div className="mt-0.5 rounded-full bg-secondary p-1 text-primary">
                      <ShieldCheck className="size-3.5" />
                    </div>
                    <span>Eco-friendly, family and garden safe solutions</span>
                  </div>

                  <div data-index={1} className="flex items-start gap-3 text-sm text-card-foreground">
                    <div className="mt-0.5 rounded-full bg-secondary p-1 text-primary">
                      <ShieldCheck className="size-3.5" />
                    </div>
                    <span>Transparent quotes with no hidden charges</span>
                  </div>

                  <div data-index={2} className="flex items-start gap-3 text-sm text-card-foreground">
                    <div className="mt-0.5 rounded-full bg-secondary p-1 text-primary">
                      <ShieldCheck className="size-3.5" />
                    </div>
                    <span>Serving all suburbs across the Auckland region</span>
                  </div>
                </div>

                <div className="mt-6 pt-5 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span>Available Monday to Saturday</span>
                  <span className="font-medium text-foreground">8:00am – 6:00pm</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}